/**
 * List of essential files that cannot be deleted from the agentic knowledge base
 * These files can be edited but not deleted
 */
export const ESSENTIAL_FILES = [
    // Core
    'prompt.md',
    'config.json',
    'CONTRIBUTING.md',

    // Identity - Writing Style
    'identity/writing_style/stylistic_signature.md',
    'identity/writing_style/tonal_profile.md',
    'identity/writing_style/vocabulary_map.md',
    'identity/writing_style/sentence_patterns.md',
    'identity/writing_style/rhythm_templates.md',

    // Identity - Creative Persona
    'identity/creative_persona/biography_source.md',
    'identity/creative_persona/values_ethics.md',
    'identity/creative_persona/cognitive_biases.md',
    'identity/creative_persona/personality_traits.md',
    'identity/creative_persona/communication_rules.md',

    // Identity - Examples
    'identity/examples/gold_samples.md',
    'identity/examples/negative_samples.md',

    // Security
    'security/guardrails.md',
    'security/forbidden_patterns.md',
    'security/allowed_references.md',
    'security/safety_policies.md',
    'security/moral_constraints.md',
    'security/doctrinal_limits.md',
    'security/escalation_rules.md',

    // Agents - Base
    'agents/base_agent.md',
    'agents/autonomous_agent.md',
    'agents/planner_agent.md',
    'agents/agent_interface.md',
    'agents/team_orchestrator.md',
    'agents/step_handler.md',
    'agents/task_manager.md',

    // Agents - Tools
    'agents/tools/calculator.md',
    'agents/tools/file_manager.md',
    'agents/tools/search_tool.md',
    'agents/tools/tool_registry.md',

    // Agents - Workflows
    'agents/workflows/code_review_chain.md',
    'agents/workflows/research_chain.md',
    'agents/workflows/multi_agent_workflow.yaml',
    'agents/workflows/workflow_executor.md',

    // API
    'api/main_api_design.md',
    'api/agent_routes.md',
    'api/tool_routes.md',
    'api/auth_routes.md',
    'api/websocket_server.md',

    // Auth
    'auth/access_control.md',
    'auth/jwt_tokens.md',
    'auth/user_manager.md',

    // Cognition - Base
    'cognition/cognitive_loop.md',
    'cognition/decision_policy.md',
    'cognition/planner.md',
    'cognition/reasoner.md',
    'cognition/state_interpreter.md',

    // Cognition - Memory
    'cognition/memory/long_term_memory.md',
    'cognition/memory/short_term_memory.md',
    'cognition/memory/memory_manager.md',

    // Config
    'config/config_concepts.md',
    'config/environments/dev_environment.md',
    'config/environments/prod_environment.md',
    'config/versioning_strategy.md',

    // Data
    'data/state/state_design.md',
    'data/knowledge_bases/vector_dbs_overview.md',
    'data/knowledge_bases/knowledge_graphs.md',

    // Deployment
    'deployment/deployment_strategies.md',
    'deployment/docker/docker_concepts.md',
    'deployment/kubernetes/kubernetes_concepts.md',
    'deployment/terraform/infra_as_code_concepts.md',

    // Docs
    'docs/architecture.md',
    'docs/api_reference.md',
    'docs/usage.md',

    // Evaluation
    'evaluation/memory_eval.md',
    'evaluation/test_harness.md',
    'evaluation/profiling_tools.md',

    // Execution
    'execution/action_resolver.md',
    'execution/controller.md',
    'execution/error_handler.md',
    'execution/executor.md',
    'execution/job_scheduler.md',
    'execution/background_worker.md',

    // Logs
    'logs/logging_strategy.md',

    // Models - Base
    'models/cache.md',
    'models/model_loader.md',
    'models/embeddings.md',
    'models/llm_wrapper.md',

    // Models - Prompts
    'models/prompts/system_prompts.md',
    'models/prompts/task_prompts.md',
    'models/prompts/prompt_templates.md',

    // Providers
    'providers/mcp_service_client.md',
    'providers/external_service_a.md',
    'providers/external_service_b.md',

    // Scripts
    'scripts/cli_concepts.md',

    // Tests
    'tests/testing_strategy.md',

    // Utils - Base
    'utils/exceptions.md',
    'utils/logger.md',
    'utils/retry.md',
    'utils/timers.md',

    // Utils - Validation
    'utils/validation/schemas.md',

    // Utils - Serialization
    'utils/serialization/agent_serializer.md',
    'utils/serialization/plan_serializer.md',

    // Utils - Formatters
    'utils/formatters/json_formatter.md',
    'utils/formatters/yaml_formatter.md',

    // Utils - Helpers
    'utils/common_helpers.md'
]

/**
 * Check if a file path is an essential file that cannot be deleted
 */
export function isEssentialFile(filePath: string): boolean {
    return ESSENTIAL_FILES.includes(filePath)
}

/**
 * Check if a folder contains only essential files (and thus cannot be deleted)
 */
export function isEssentialFolder(folderPath: string): boolean {
    // Essential folders are those that contain essential files
    const essentialFolders = [
        'identity',
        'identity/writing_style',
        'identity/creative_persona',
        'identity/examples',
        'security',
        'agents',
        'agents/tools',
        'agents/workflows',
        'api',
        'auth',
        'cognition',
        'cognition/memory',
        'config',
        'config/environments',
        'data',
        'data/state',
        'data/knowledge_bases',
        'deployment',
        'deployment/docker',
        'deployment/kubernetes',
        'deployment/terraform',
        'docs',
        'evaluation',
        'execution',
        'logs',
        'models',
        'models/prompts',
        'providers',
        'scripts',
        'tests',
        'utils',
        'utils/validation',
        'utils/serialization',
        'utils/formatters'
    ]

    return essentialFolders.includes(folderPath)
}
